// frontend/src/components/auditor/AuditorOverview.js
// Summary dashboard for the auditor portal showing key metrics at a glance.

import React, { useState, useEffect, useCallback } from 'react';
import { fetchReport } from '../../utils/auditorApi';

const StatCard = ({ label, value, sub, color = 'teal' }) => {
  const colors = {
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    indigo: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color] || colors.teal}`}>
      <p className="text-xs uppercase tracking-wider font-medium opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value ?? '—'}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
};

export default function AuditorOverview({ authAxios, dateRange, firmName }) {
  const [financial, setFinancial] = useState(null);
  const [caseload, setCaseload] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = { start_date: dateRange.startDate, end_date: dateRange.endDate };
    const [fin, cs] = await Promise.all([
      fetchReport(authAxios, 'reports/financial/', params),
      fetchReport(authAxios, 'reports/caseload/', params),
    ]);
    if (fin) setFinancial(fin);
    if (cs) setCaseload(cs);
    setLoading(false);
  }, [authAxios, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (v) => {
    if (v == null) return '—';
    return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Audit Overview</h2>
        <p className="text-sm text-gray-500 mt-1">
          {firmName} &middot; {dateRange.startDate} to {dateRange.endDate}
        </p>
      </div>

      {/* Financial metrics */}
      <section>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Financial Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Invoiced" value={`P ${fmt(financial?.total_invoiced_amount)}`} color="blue" />
          <StatCard label="Payments Received" value={`P ${fmt(financial?.paid_invoices_amount)}`} color="green" />
          <StatCard label="Outstanding" value={`P ${fmt(financial?.outstanding_invoices_amount)}`} color="amber" />
          <StatCard label="Work-in-Progress" value={`P ${fmt(financial?.work_in_progress_amount)}`} color="purple" />
        </div>
      </section>

      {/* Billing metrics */}
      <section>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Billing &amp; Time</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Billable Hours" value={fmt(financial?.total_billable_hours)} sub="hours" color="indigo" />
          <StatCard label="Total Expenses" value={`P ${fmt(financial?.total_expenses)}`} color="red" />
          <StatCard label="Turnover excl. Group K" value={`P ${fmt(financial?.total_turnover_excl_group_k)}`} color="teal" />
          <StatCard label="Total Cases" value={caseload?.total_cases ?? '—'} color="slate" />
        </div>
      </section>

      {/* Case metrics */}
      {caseload && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Case Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.isArray(caseload.cases_by_status) && caseload.cases_by_status.map((entry) => (
              <StatCard
                key={entry.status}
                label={`Cases: ${entry.status || 'Unknown'}`}
                value={entry.count}
                color={entry.status === 'Open' ? 'teal' : entry.status === 'Closed' ? 'slate' : 'amber'}
              />
            ))}
            <StatCard label="Avg. Days to Close" value={caseload.average_days_to_close_case != null ? Math.round(caseload.average_days_to_close_case) : '—'} sub="days" color="blue" />
          </div>
        </section>
      )}

      {/* Guidance text */}
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-5 text-sm text-teal-800">
        <p className="font-medium mb-1">How to use this portal</p>
        <p className="text-teal-700">
          Use the tabs above to navigate between financial records, trust accounts, time &amp; billing,
          tax reports, cases &amp; clients, compliance data, and the full activity log. All data is
          read-only. Use the date range picker in the header to adjust the audit period. You can export
          data to CSV from any table.
        </p>
      </div>
    </div>
  );
}
